import { weekdayOf } from '@kronos/domain'
import type { GridCell, LocalDate, ScheduleGrid } from '@kronos/domain'
import type { ComeInRequestView, TechnicianView } from '../../data/schedule'
import type { Ask } from './RequestForm'
import { CellRequests } from './CellRequests'
import { shortWindow } from './shared'

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * The desk view: one row per technician, one column per day. A cell is a
 * button — what they said on top, what they were asked under it — and
 * clicking it is "ask them in on this day". Hidden under 900px, where
 * `TeamDayList` shows the same grid by day.
 */
export function TeamGrid({
  grid,
  today,
  timeZone,
  onPick,
}: {
  grid: ScheduleGrid<ComeInRequestView, TechnicianView>
  today: LocalDate
  timeZone: string
  onPick: (ask: Ask) => void
}): JSX.Element {
  return (
    <div className="panel sched-grid-wrap">
      <div className="table-scroll">
        <table className="sched-grid">
          <thead>
            <tr>
              <th className="sched-grid__who" scope="col">
                Technician
              </th>
              {grid.dates.map((date) => (
                <th key={date} scope="col" className={headClass(date, today)}>
                  <span className="sched-grid__wd">{WEEKDAY[weekdayOf(date)]}</span>
                  <span className="sched-grid__dn">{Number(date.slice(8))}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map(({ technician, cells }) => (
              <tr key={technician.id}>
                <th scope="row" className="sched-grid__who">
                  {technician.name}
                </th>
                {cells.map((cell) => (
                  <td key={cell.date} className={headClass(cell.date, today)}>
                    <Cell
                      cell={cell}
                      past={cell.date < today}
                      timeZone={timeZone}
                      label={`${technician.name}, ${cell.date}`}
                      onPick={() => onPick({ technicianId: technician.id, date: cell.date })}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function headClass(date: LocalDate, today: LocalDate): string {
  const wd = weekdayOf(date)
  return [date === today ? 'is-today' : '', wd === 0 || wd === 6 ? 'is-weekend' : ''].filter(Boolean).join(' ')
}

function Cell({
  cell,
  past,
  timeZone,
  label,
  onPick,
}: {
  cell: GridCell<ComeInRequestView>
  past: boolean
  timeZone: string
  label: string
  onPick: () => void
}): JSX.Element {
  const a = cell.availability
  const state = a ? (a.window ? 'available' : 'off') : 'unset'
  return (
    <button
      className={`sched-cell sched-cell--${state}`}
      disabled={past}
      onClick={onPick}
      aria-label={`${label}: ${a ? (a.window ? shortWindow(a.window) : 'off') : 'not set'}. Ask to come in.`}
      title={a?.note ?? undefined}
    >
      <span className="sched-cell__avail">{a ? (a.window ? shortWindow(a.window) : 'Off') : ''}</span>
      <CellRequests requests={cell.requests} timeZone={timeZone} />
    </button>
  )
}
